import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { axiosLib } from '@/lib/axios';
import { getColocApiCallProperties } from '@/lib/coloc-api';
import { showToast } from '@/services/toast-service';

interface Colocation {
  id: number;
  slug: string;
  is_watched: boolean;
  name: string;
  price: string;
  available_on: string;
  image: string;
  virtual_tour_url?: string;
}

interface ColocationsResponse {
  colocations: Colocation[];
}

export const useWatchToggle = (citySlug: string) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const handleWatchToggle = async (colocationSlug: string): Promise<boolean> => {
    try {
      // Get current data from cache using the same query key as useColocations
      const queryKey = ['colocations', 'coming-soon', citySlug.toLowerCase()];
      const currentData = queryClient.getQueryData<ColocationsResponse>(queryKey);

      if (!currentData?.colocations) {
        showToast.error({
          title: String(t('js.components.watchlist.error')),
          text: String(t('js.components.watchlist.error_updating_watchlist')),
        });
        return false;
      }

      // Find the colocation and its current watch status
      const colocation = currentData.colocations.find((c) => c.slug === colocationSlug);
      if (!colocation) return false;

      const isWatched = colocation.is_watched;

      // Optimistically update the cache
      queryClient.setQueryData<ColocationsResponse>(queryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          colocations: old.colocations.map((c) =>
            c.slug === colocationSlug ? { ...c, is_watched: !isWatched } : c
          ),
        };
      });

      if (isWatched) {
        await axiosLib.delete(`/colocations/${colocationSlug}/unwatch`, {
          ...getColocApiCallProperties(),
        });
        showToast.success({
          title: String(t('js.components.watchlist.removed_from_watchlist')),
          text: String(t('js.components.watchlist.property_removed_from_watchlist')),
        });
      } else {
        await axiosLib.post(`/colocations/${colocationSlug}/watch`, null, {
          ...getColocApiCallProperties(),
        });
        showToast.success({
          title: String(t('js.components.watchlist.added_to_watchlist')),
          text: String(t('js.components.watchlist.property_added_to_watchlist')),
        });
      }

      return true;
    } catch (error: any) {
      // Revert the optimistic update on error
      queryClient.invalidateQueries({
        queryKey: ['colocations', 'coming-soon', citySlug.toLowerCase()],
      });

      if (error.response?.status === 401) {
        const redirectUrl = error.response?.data?.redirect_url;
        if (redirectUrl) {
          window.location.href = redirectUrl;
          return false;
        }
      }
      showToast.error({
        title: String(t('js.components.watchlist.error')),
        text: String(t('js.components.watchlist.error_updating_watchlist')),
      });
      return Promise.reject(error);
    }
  };

  return { handleWatchToggle };
};
